import {EventEmitter2} from "./util/EventEmitter2";
import {ScreenElevationData} from "../../../../api/elevationTool/ScreenElevationData";
import {DoorElevationData} from "../../../../api/elevationTool/DoorElevationData";
import {HardwareElevationData} from "../../../../api/elevationTool/HardwareElevationData";

// one full-document undo state: the three elevation DTOs, exactly what save() serializes,
// plus the nominal sizes. Those live on the opening rather than in a DTO (see
// data-json-dtos.md), but the tool edits them, so undo has to carry them or an unrelated
// undo would silently revert a size edit.
export type ElevationSnapshot = {
    screen: ScreenElevationData;
    door: DoorElevationData;
    hardware: HardwareElevationData;
    dimensions: SnapshotDimensions;
};

// the nominal sizes in drawing units, as the frame spec holds them.
export type SnapshotDimensions = {nominalWidth: number; inActiveDoorWidth: number; nominalHeight: number};

const MAX_ENTRIES = 50;
const CAPTURE_DEBOUNCE_MS = 500;

// snapshot-based undo/redo stack for the elevation tool. Captures are scheduled off the
// same coarse change signals as dirty tracking (see ElevationApp.wireDirtyTracking) and
// debounced so a drag's per-frame event burst collapses into one entry. Snapshots are
// taken/applied through the constructor callbacks so this class stays decoupled from
// ElevationApp (which owns the models and imports this).
export class UndoHistory {
    canUndo = new EventEmitter2<boolean>(false, "undo.canUndo");
    canRedo = new EventEmitter2<boolean>(false, "undo.canRedo");

    // JSON-serialized snapshots; entries[index] is always the current document state
    // (the baseline sits at index 0). Strings make dedup a cheap equality check.
    private entries: string[] = [];
    private index = -1;
    private timer: number | null = null;

    // true while a restore runs, so change events it triggers can't re-capture.
    private suspended = false;

    constructor(
        private takeSnapshot: () => ElevationSnapshot | null,
        private applySnapshot: (snap: ElevationSnapshot) => void,
    ) {}

    // clear everything and capture the freshly-loaded state as the baseline. Called at
    // the end of load(); undo is never allowed to cross a load boundary.
    reset() {
        this.cancelTimer();
        this.entries = [];
        this.index = -1;
        this.captureNow();
    }

    // debounced capture: coalesces a drag burst / multi-field edit into one entry.
    scheduleCapture() {
        if(this.suspended) return;
        this.cancelTimer();
        this.timer = window.setTimeout(() => {
            this.timer = null;
            this.captureNow();
        }, CAPTURE_DEBOUNCE_MS);
    }

    captureNow() {
        if(this.suspended) return;
        this.cancelTimer();

        const snap = this.takeSnapshot();
        if(!snap) return;

        const serialized = JSON.stringify(snap);
        if(serialized === this.entries[this.index]) return;

        // a new edit invalidates the redo tail
        this.entries = this.entries.slice(0, this.index + 1);
        this.entries.push(serialized);
        this.index++;

        if(this.entries.length > MAX_ENTRIES) {
            this.entries.shift();
            this.index--;
        }

        this.emitState();
    }

    undo() {
        this.flush();
        if(this.index <= 0) return;
        this.index--;
        this.restoreTo(this.entries[this.index]);
    }

    redo() {
        this.flush();
        if(this.index >= this.entries.length - 1) return;
        this.index++;
        this.restoreTo(this.entries[this.index]);
    }

    dispose() {
        this.cancelTimer();
    }

    // commit an in-flight debounced burst as its own entry, so undo steps over it
    // (making it redoable) instead of losing half of it.
    private flush() {
        if(this.timer !== null) this.captureNow();
    }

    private restoreTo(serialized: string) {
        this.suspended = true;
        try {
            this.applySnapshot(JSON.parse(serialized));
        } finally {
            this.suspended = false;
        }
        this.emitState();
    }

    private emitState() {
        this.canUndo.emit(this.index > 0);
        this.canRedo.emit(this.index < this.entries.length - 1);
    }

    private cancelTimer() {
        if(this.timer === null) return;
        window.clearTimeout(this.timer);
        this.timer = null;
    }
}
